/**
 * V113-7-0 — React data hooks for the Track 7 Monitoring screens. Reads follow useWorkspaceQuery's
 * `{ data, loading, error, refetch }` shape; writes are react-query mutations that refresh every
 * monitoring read of the workspace (one key root: ['monitoring', slug, …]) and expose the server error
 * normalized by monitoringErrorOf(). Keys live here, not in lib/query-keys.ts, so parallel tracks don't
 * collide on that file.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useWorkspace } from '../lib/workspace';
import { useWorkspaceQuery } from './useWorkspaceQuery';
import {
  acceptCandidate,
  declareSystemClass,
  getAnomalyThresholds,
  getConfigurationItem,
  getMonitoringPlan,
  getMonitoringPlanPackage,
  getMonitoringTimeline,
  getPackValidity,
  getSystemMonitoring,
  issueMonitoringFeed,
  linkConfigurationItemToSystem,
  listConfigurationItems,
  listMonitoringFeeds,
  listMonitoringPlanPackages,
  maskToken,
  mergeConfigurationItem,
  monitoringErrorOf,
  putAnomalyThresholds,
  retireConfigurationItem,
  revokeMonitoringFeed,
  rotateMonitoringFeedLink,
  setConfigurationItemScope,
  signMonitoringPlan,
  updateConfigurationItem,
  verifyConfigurationItemMethod,
  type CiScope,
  type ConfigurationItemListQuery,
  type ConfigurationItemPatch,
  type IssueFeedInput,
  type IssuedFeed,
  type MonitoringError,
  type SystemClass,
  type TimelineQuery,
} from '../lib/monitoring-api';
import { getMonitoringSummary } from '../lib/monitoring-summary';
import { monitoringCapabilities, type CapabilityContext } from '../lib/monitoring-capabilities';
import type { AnomalyThresholdOverrideInput } from '@aigovops/core';

export const monitoringKeys = {
  root: (slug: string) => ['monitoring', slug] as const,
  summary: (slug: string) => ['monitoring', slug, 'summary'] as const,
  badge: (slug: string) => ['monitoring', slug, 'badge'] as const,
  system: (slug: string, systemId: string) => ['monitoring', slug, 'system', systemId] as const,
  timeline: (slug: string, systemId: string, q: Omit<TimelineQuery, 'cursor'>) => ['monitoring', slug, 'timeline', systemId, q] as const,
  items: (slug: string, q: ConfigurationItemListQuery) => ['monitoring', slug, 'configuration-items', q] as const,
  item: (slug: string, id: string) => ['monitoring', slug, 'configuration-item', id] as const,
  packValidity: (slug: string) => ['monitoring', slug, 'pack-validity'] as const,
  feeds: (slug: string) => ['monitoring', slug, 'feeds'] as const,
  plan: (slug: string, systemId: string) => ['monitoring', slug, 'plan', systemId] as const,
  planPackages: (slug: string, systemId: string) => ['monitoring', slug, 'plan-packages', systemId] as const,
  planPackage: (slug: string, systemId: string, packageId: string) => ['monitoring', slug, 'plan-package', systemId, packageId] as const,
  anomaly: (slug: string, systemId: string) => ['monitoring', slug, 'anomaly-thresholds', systemId] as const,
};

/** Don't retry what retrying cannot fix (404 / 403 / 422); everything else once. */
const retry = (count: number, err: Error) => {
  const k = monitoringErrorOf(err).kind;
  return !(k === 'not_found' || k === 'forbidden' || k === 'validation') && count < 1;
};

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/** Cross-system table: GET /monitoring/systems (one read; `truncated` past the server's cap). */
export const useMonitoringSummary = () =>
  useWorkspaceQuery(monitoringKeys.summary, (slug) => getMonitoringSummary(slug), { retry });

export const useSystemMonitoring = (systemId: string | null | undefined) =>
  useWorkspaceQuery((slug) => monitoringKeys.system(slug, systemId ?? ''), (slug) => getSystemMonitoring(slug, systemId!), { enabled: !!systemId, retry });

export const useConfigurationItems = (q: ConfigurationItemListQuery = {}) =>
  useWorkspaceQuery((slug) => monitoringKeys.items(slug, q), (slug) => listConfigurationItems(slug, q), { retry });

export const useConfigurationItem = (id: string | null | undefined) =>
  useWorkspaceQuery((slug) => monitoringKeys.item(slug, id ?? ''), (slug) => getConfigurationItem(slug, id!), { enabled: !!id, retry });

export const usePackValidity = () =>
  useWorkspaceQuery(monitoringKeys.packValidity, (slug) => getPackValidity(slug), { retry });

/** Owner / admin only: pass `enabled: false` for other roles so the page never asks for a 403. */
export const useMonitoringFeeds = (enabled = true) =>
  useWorkspaceQuery(monitoringKeys.feeds, (slug) => listMonitoringFeeds(slug), { enabled, retry });

export const useMonitoringPlan = (systemId: string | null | undefined) =>
  useWorkspaceQuery((slug) => monitoringKeys.plan(slug, systemId ?? ''), (slug) => getMonitoringPlan(slug, systemId!), { enabled: !!systemId, retry });

export const useMonitoringPlanPackages = (systemId: string | null | undefined) =>
  useWorkspaceQuery((slug) => monitoringKeys.planPackages(slug, systemId ?? ''), (slug) => listMonitoringPlanPackages(slug, systemId!), { enabled: !!systemId, retry });

export const useMonitoringPlanPackage = (systemId: string | null | undefined, packageId: string | null | undefined) =>
  useWorkspaceQuery(
    (slug) => monitoringKeys.planPackage(slug, systemId ?? '', packageId ?? ''),
    (slug) => getMonitoringPlanPackage(slug, systemId!, packageId!),
    { enabled: !!systemId && !!packageId, retry });

export const useAnomalyThresholds = (systemId: string | null | undefined) =>
  useWorkspaceQuery((slug) => monitoringKeys.anomaly(slug, systemId ?? ''), (slug) => getAnomalyThresholds(slug, systemId!), { enabled: !!systemId, retry });

/**
 * The drift timeline, keyset-paginated (newest first). `entries` is every loaded page flattened;
 * `loadMore()` fetches the next page while `hasMore`. Changing the filter starts over.
 */
export function useMonitoringTimeline(systemId: string | null | undefined, filter: Omit<TimelineQuery, 'cursor'> = {}) {
  const { workspace } = useWorkspace();
  const slug = workspace?.slug ?? '';
  const query = useInfiniteQuery({
    queryKey: monitoringKeys.timeline(slug, systemId ?? '', filter),
    queryFn: ({ pageParam }) => getMonitoringTimeline(slug, systemId!, { ...filter, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !!slug && !!systemId,
    retry,
  });
  const entries = useMemo(() => query.data?.pages.flatMap((p) => p.entries) ?? [], [query.data]);
  return {
    entries,
    window: query.data?.pages[0]?.window ?? null,
    loading: query.isLoading,
    loadingMore: query.isFetchingNextPage,
    hasMore: !!query.hasNextPage,
    loadMore: () => query.fetchNextPage(),
    error: query.error ? monitoringErrorOf(query.error) : null,
    refetch: query.refetch,
    query,
  };
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

function useMonitoringMutation<TInput, TOutput>(fn: (slug: string, input: TInput) => Promise<TOutput>) {
  const { workspace } = useWorkspace();
  const slug = workspace?.slug ?? '';
  const qc = useQueryClient();
  const m = useMutation<TOutput, Error, TInput>({
    mutationFn: (input) => fn(slug, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: monitoringKeys.root(slug) }),
  });
  return { ...m, failure: m.error ? monitoringErrorOf(m.error) : null as MonitoringError | null };
}

export const useAcceptCandidate = (systemId: string) =>
  useMonitoringMutation((slug, candidateId: string) => acceptCandidate(slug, systemId, candidateId));

/** Declare a class, or confirm the inferred one by passing it. */
export const useDeclareSystemClass = (systemId: string) =>
  // V113-7 (W-21): pass `{ system_class, reason?, review_by? }` to record why and when to re-review; a bare class still works.
  useMonitoringMutation((slug, input: SystemClass | { system_class: SystemClass; reason?: string; review_by?: string }) => (typeof input === 'string'
    ? declareSystemClass(slug, systemId, input)
    : declareSystemClass(slug, systemId, input.system_class, { reason: input.reason, review_by: input.review_by })));

export const useUpdateConfigurationItem = (id: string) =>
  useMonitoringMutation((slug, patch: ConfigurationItemPatch) => updateConfigurationItem(slug, id, patch));

export const useLinkConfigurationItem = (id: string) =>
  useMonitoringMutation((slug, systemId: string) => linkConfigurationItemToSystem(slug, id, systemId));

export const useSetConfigurationItemScope = (id: string) =>
  // V113-7 (836): out of scope carries a non-empty reason (400 without one); a failing item needs owner / admin.
  useMonitoringMutation((slug, input: { systemId: string; scope: CiScope; reason?: string }) => (input.scope === 'out_of_scope'
    ? setConfigurationItemScope(slug, id, input.systemId, 'out_of_scope', input.reason ?? '')
    : setConfigurationItemScope(slug, id, input.systemId, 'in_scope')));

export const useMergeConfigurationItem = (id: string) =>
  useMonitoringMutation((slug, intoComponentId: string) => mergeConfigurationItem(slug, id, intoComponentId));

export const useRetireConfigurationItem = (id: string) =>
  useMonitoringMutation((slug, _: void) => retireConfigurationItem(slug, id));

export const useVerifyMethod = (id: string) =>
  useMonitoringMutation((slug, methodId: string) => verifyConfigurationItemMethod(slug, id, methodId));

export const useRevokeFeed = () =>
  useMonitoringMutation((slug, grantId: string) => revokeMonitoringFeed(slug, grantId));

export const useRotateFeedLink = () =>
  useMonitoringMutation((slug, grantId: string) => rotateMonitoringFeedLink(slug, grantId));

export const useSignMonitoringPlan = (systemId: string) =>
  useMonitoringMutation((slug, _: void) => signMonitoringPlan(slug, systemId));

export const usePutAnomalyThresholds = (systemId: string) =>
  useMonitoringMutation((slug, overrides: AnomalyThresholdOverrideInput[]) => putAnomalyThresholds(slug, systemId, overrides));

/**
 * Issue a shared feed. The FULL one-time token is held in a ref (never in the query cache, never read
 * back from the DOM): `copyToken()` writes it to the clipboard from that ref, `tokenDisplay` is a masked
 * form for the screen, and `forget()` drops it (also on unmount).
 */
export function useIssueFeed() {
  const m = useMonitoringMutation((slug, input: IssueFeedInput) => issueMonitoringFeed(slug, input));
  const tokenRef = useRef<string | null>(null);
  const [issued, setIssued] = useState<Omit<IssuedFeed, 'token'> | null>(null);
  useEffect(() => () => { tokenRef.current = null; }, []);
  const issue = useCallback(async (input: IssueFeedInput) => {
    const out = await m.mutateAsync(input);
    tokenRef.current = out.token;
    const { token: _token, ...rest } = out;
    setIssued(rest);
    return rest;
  }, [m.mutateAsync]); // eslint-disable-line react-hooks/exhaustive-deps
  const copyToken = useCallback(async () => {
    const t = tokenRef.current;
    if (!t) return false;
    await navigator.clipboard.writeText(t);
    return true;
  }, []);
  const forget = useCallback(() => { tokenRef.current = null; setIssued(null); m.reset(); }, [m.reset]); // eslint-disable-line react-hooks/exhaustive-deps
  return {
    issue,
    issued,
    /** Masked; for display only. */
    tokenDisplay: issued && tokenRef.current ? maskToken(tokenRef.current) : null,
    /** The full token, for a "reveal" field or a download — read it here, never from rendered text. */
    getToken: () => tokenRef.current,
    copyToken,
    forget,
    isPending: m.isPending,
    failure: m.failure,
  };
}

// ---------------------------------------------------------------------------
// Capabilities
// ---------------------------------------------------------------------------

/** The current member's monitoring capabilities (client mirror of the server gates). */
export function useMonitoringCapabilities(extra: Omit<CapabilityContext, 'role'> = {}) {
  const { role } = useWorkspace();
  return useMemo(() => monitoringCapabilities({ role, ...extra }), [role, extra.leadsAssetProgram]); // eslint-disable-line react-hooks/exhaustive-deps
}
